import { curatedLeagueName } from "../../coworlds/coworld-curation";
import {
  isUnpublishedLeague,
  leagueRoute,
  slugifyLeagueName,
} from "../../leagues/league-catalog";
import { watchableLeagues } from "../../leagues/watchable-leagues";
import { apiGet } from "@softmax/observatory-client/client";
import type { LeaguePublic, Schemas } from "@softmax/observatory-client/types";
import { queryOptions } from "@tanstack/react-query";
import type { DirectoryEntryLike } from "./coworld-switcher-data";

export type LeagueDirectoryEntry = DirectoryEntryLike & {
  readonly unpublished: boolean;
};

const directoryStaleMs = 5 * 60_000;

export function leagueDirectoryQueryOptions({
  viewerId,
}: {
  readonly viewerId: string | null;
}) {
  return queryOptions({
    queryKey: ["league-directory", viewerId ?? "anonymous"],
    queryFn: async ({ signal }) => {
      const [entries, owned] = await Promise.all([
        fetchLeagueDirectory(signal),
        viewerId ? fetchOwnedLeagueIds(signal) : [],
      ]);
      return { entries, owned };
    },
    staleTime: directoryStaleMs,
  });
}

/** Viewer-visible leagues use the same filtering and addresses as their destinations. */
async function fetchLeagueDirectory(
  signal: AbortSignal,
): Promise<LeagueDirectoryEntry[]> {
  const leagues = watchableLeagues(
    await apiGet<LeaguePublic[]>("/v2/leagues", {
      elevatedPrivileges: false,
      signal,
    }),
  );
  return leagues.map((league) => ({
    league_id: league.id,
    default_league_id: league.game.default_league_id ?? null,
    route: leagueRoute(league),
    corpusSlug: slugifyLeagueName(league.game.coworld_name),
    coworld: league.game.coworld_name,
    name: curatedLeagueName(league),
    unpublished: isUnpublishedLeague(league),
  }));
}

async function fetchOwnedLeagueIds(signal: AbortSignal): Promise<string[]> {
  const leagues = await apiGet<LeaguePublic[]>("/v2/leagues?owned=true", {
    elevatedPrivileges: false,
    signal,
  });
  return leagues.map((league) => league.id);
}

export function leaguePortfolioQueryOptions() {
  return queryOptions({
    queryKey: ["viewer", "league-portfolio", { elevatedPrivileges: false }],
    queryFn: ({ signal }) =>
      apiGet<Schemas["LeaguePortfolioSeatPublic"][]>(
        "/v2/league-policy-memberships/portfolio",
        { elevatedPrivileges: false, signal },
      ),
  });
}
